import type {
  RankingType,
  RankingData,
  RankingTableEntry,
} from "lib/fetchers/rankings";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";
import { PaywallWindow } from "lib/types/paywalls";

import Table from "./components/table/table";
import { getRankingConfig } from "./components/table/components/pageConfigs";
import { processNumericEntry } from "./components/table/utils";
import { TableDataType } from "./components/table/types";
import DetailHero from "content-ui/leaderboards/components/detailHero/detailHero";
import { LeaderboardPage } from "lib/types/leaderboards";

type MixPanelProps = {
  [key: string]: string;
};

const RankingView = ({
  ranking,
  previewMode: _previewMode,
  blur = false,
  downloadPaywall = null,
}: {
  ranking: RankingData;
  previewMode: boolean;
  blur: boolean;
  downloadPaywall?: PaywallWindow | null;
}) => {
  const rankingConfig = getRankingConfig(
    ranking?.rankingType?.toLowerCase() as RankingType
  ); // Ideally all of this data processing would be done in the Dato plugin
  const optionalColumns = ranking.optionalColumns || [];
  const filterType = ranking.customFilter;

  const formattedTableData = ranking.rankingsTable.map(
    (entry: RankingTableEntry, index: number) => {
      entry.rank = index + 1;

      if ("company" in entry) {
        // Add a location field to all the entries
        entry.location = [entry.city, entry.country];
        // Combine company name and url into one entry
        entry.companyAndUrl = [entry.company, entry.website];
        // Sanitise data
        entry.founded = processNumericEntry(entry.founded);
        entry.lastRoundSize = processNumericEntry(entry.lastRoundSize);
        entry.totalFunding = processNumericEntry(entry.totalFunding);
        return entry;
      }
      if ("investorName" in entry) {
        entry.location = [entry.city, entry.country];
        // Combine company name and url into one entry
        entry.investorNameAndUrl = [entry.investorName, entry.investorUrl];
        // Sanitise data
        entry.latestFundSize = processNumericEntry(entry.latestFundSize);
        entry.fundLaunchYear = processNumericEntry(entry.fundLaunchYear);
        entry.dealCount = processNumericEntry(entry.dealCount);
        return entry;
      }
      return entry;
    }
  );
  // Change the columns that show, based on user preference in Dato
  const customColumnConfig = rankingConfig.columns.filter((el) => {
    const headings = [...rankingConfig.mandatoryColumns, ...optionalColumns];

    // Always include location column if we have city/country data (it's created dynamically)
    if (el.dataName === "location") {
      return true;
    }

    return headings.includes(el.dataName as string);
  });
  rankingConfig.columns = customColumnConfig;

  const { track } = useMixpanelTracking();

  const handleMixpanelTracking = async (
    event: string,
    mixPanelProps: MixPanelProps
  ) => await track(event, mixPanelProps);

  const mapped = {
    id: ranking.id || "",
    slug: ranking.slug || "",
    title: ranking.title || "",
    subtitle: ranking.subtitle || "",
    description: ranking.description || "",
    indexDescription: ranking.indexPageDescription || "",
    publishDate: ranking.lastUpdated || "",
    publishState: "",
    showPublishDate: !!ranking.lastUpdated,
    image: ranking.rankingImage || {},
    sponsors:
      ranking.sponsors?.length > 0
        ? ranking.sponsors
        : [
            {
              id: ranking.sponsorName || "",
              name: ranking.sponsorName || "",
              logo: ranking.sponsorImage || "",
              url: ranking.sponsorUrl || "",
              description: ranking.sponsorDescription || "",
              sponsorshipType: ranking.sponsorshipType || "",
            },
          ],
  } as LeaderboardPage;
  return (
    <div className={"relative bg-[#F4F4F6] px-5 lg:px-0"}>
      {blur && (
        <div className={"absolute inset-0 z-10 bg-white/50 backdrop-blur-md"} />
      )}
      <div className={"mx-auto max-w-[1040px]"}>
        <DetailHero
          title={ranking.title}
          subtitle={ranking.subtitle}
          data={mapped}
          onTrackMixpanel={handleMixpanelTracking}
          backLink={{
            href: "/rankings",
            label: "Back to Rankings",
            ariaLabel: "Go to rankings",
          }}
          mixpanelEventType="Ranking"
          showPublishedDate={false}
        />
      </div>
      <main>
        <div className="mx-auto max-w-[1040px]">
          <div className="pb-4xl">
            <Table
              tableConfig={rankingConfig}
              tableEntries={formattedTableData as TableDataType[]}
              filterType={filterType}
              disableDownload={blur}
              isPro={ranking.isPro}
              downloadPaywall={downloadPaywall}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default RankingView;
